/**
 * Flutter WebView通信ブリッジ
 *
 * Flutter InAppWebViewとの通信を抽象化する
 * 直接window.flutter_inappwebviewを呼び出さず、このモジュール経由で通信する
 */

import type {
  FlutterInAppWebView,
  FlutterChannel,
} from '../types/flutter';
// グローバル型定義を有効化
import '../types/flutter';

/**
 * WebView環境かどうかを判定（webview_flutter用）
 * webview_flutterではJavaScriptChannelがwindowオブジェクトに直接追加される
 */
export const isWebViewEnvironment = (): boolean => {
  // webview_flutterの場合、FlutterChannelやInquiryHandlerなどがwindowに追加される
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const win = window as any;
  return typeof window !== 'undefined' && (
    !!win.FlutterChannel ||
    !!win.InquiryHandler ||
    !!win.flutter_inappwebview
  );
};

/**
 * Flutterのハンドラを呼び出す
 * @param handlerName ハンドラ名
 * @param args 引数
 * @returns Promise<unknown>
 */
export const callFlutterHandler = async (
  handlerName: string,
  ...args: unknown[]
): Promise<unknown> => {
  if (!isWebViewEnvironment()) {
    console.warn(`Flutter handler "${handlerName}" called outside WebView environment`);
    return undefined;
  }

  const win = window as unknown as { flutter_inappwebview: FlutterInAppWebView };
  return win.flutter_inappwebview.callHandler(handlerName, ...args);
};

/**
 * FlutterChannel経由でメッセージを送信
 * @param message メッセージ
 */
export const postMessageToFlutter = (message: string): void => {
  const win = window as unknown as { FlutterChannel?: FlutterChannel };
  if (win.FlutterChannel) {
    win.FlutterChannel.postMessage(message);
  }
};

/**
 * 指定されたJavaScriptChannel経由でメッセージを送信（webview_flutter用）
 * @param channelName チャンネル名
 * @param message メッセージ
 */
export const postMessageToChannel = (channelName: string, message: string): void => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const win = window as any;
  const channel = win[channelName];
  if (channel?.postMessage) {
    channel.postMessage(message);
  }
};

/**
 * アプリブリッジ - 共通操作のラッパー
 */
export const appBridge = {
  /**
   * 前の画面に戻る
   */
  goBack: () => callFlutterHandler('goBack'),

  /**
   * Flutter のダッシュボード（ホーム）画面に戻る。
   * ナビゲーションスタックを全クリアして遷移するため、
   * 戻るボタンで前画面に復帰できなくなる。
   *
   * 用途: ドクタールートに切り替わった等で WebView 側から
   * 強制的にカルテのホーム画面へ復帰させたい場合。
   */
  navigateToDashboard: () => postMessageToFlutter('navigateToDashboard'),

  /**
   * 処理完了を通知
   * @param result 結果データ
   */
  onComplete: (result: Record<string, unknown>) =>
    callFlutterHandler('onComplete', result),

  /**
   * エラーを通知
   * @param error エラー情報
   */
  onError: (error: { code: string; message: string }) =>
    callFlutterHandler('onError', error),

  /**
   * アプリ内レビューダイアログを表示
   */
  showReview: () => postMessageToFlutter('showReview'),

  /**
   * ローディング表示を開始する（画面遷移開始時に呼び出す）
   */
  showLoading: () => postMessageToFlutter('showLoading'),

  /**
   * ローディング表示を消す（データ取得完了時に呼び出す）
   */
  hideLoading: () => postMessageToFlutter('hideLoading'),

  /**
   * Flutter側の戻るボタンを非表示にする
   */
  hideBackButton: () => postMessageToFlutter('hideBackButton'),

  /**
   * Flutter側の戻るボタンを表示する
   */
  showBackButton: () => postMessageToFlutter('showBackButton'),

  /**
   * お問い合わせ詳細画面へ遷移
   * @param inquiryId お問い合わせID
   */
  navigateToInquiry: (inquiryId: string) =>
    callFlutterHandler('InquiryHandler', { action: 'navigate', inquiryId }),

  /**
   * 点検詳細画面へ遷移
   * @param inspectionId 点検ID
   */
  navigateToInspection: (inspectionId: string) =>
    callFlutterHandler('InspectionHandler', { action: 'navigate', inspectionId }),

  /**
   * 外部URLを外部アプリ（PDF以外の他用途向け）で開く
   *
   * Android WebView は `target="_blank"` を単純には処理できない。
   * Flutter 側で `url_launcher` を使って外部アプリで開く。
   * @param url 開くURL
   */
  openExternalUrl: (url: string) =>
    postMessageToFlutter(`openExternalUrl:${url}`),

  /**
   * アプリ内 PDF ビューアで PDF を開く（WebView 環境のみ）
   *
   * Flutter 側で `PdfViewerScreen` をプッシュ表示する。
   * URL は UI に一切表示されず、アプリから離脱しない。
   *
   * メッセージ形式: `openPdfViewer:{"url":"...","title":"..."}`
   * 区切り文字衝突を避けるため JSON 文字列化する。
   *
   * セキュリティ二重防壁: Flutter 側と同じく http/https 以外を拒否する。
   * 不正スキーム（javascript:/file:/data: 等）は postMessage を送信せず warn のみ。
   *
   * @param url PDF の URL（http/https 限定）
   * @param title 表示タイトル（省略時は空文字）
   */
  openPdfViewer: (url: string, title?: string): void => {
    // 制御文字（NULL/改行/タブ等）を含む URL を拒否（URL 偽装対策）
    // WHATWG URL パーサーは一部の制御文字を許容してしまうため明示的に弾く
    // eslint-disable-next-line no-control-regex
    if (/[\x00-\x1f\x7f]/.test(url)) {
      // eslint-disable-next-line no-console
      console.warn('[appBridge.openPdfViewer] Rejected URL with control chars');
      return;
    }
    // URL を WHATWG URL パーサーで検証（不正形式を弾く）
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      // eslint-disable-next-line no-console
      console.warn('[appBridge.openPdfViewer] Invalid URL');
      return;
    }
    // プロトコルホワイトリスト: Flutter 側と同じく http/https のみ
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      // eslint-disable-next-line no-console
      console.warn('[appBridge.openPdfViewer] Rejected non-http(s) URL');
      return;
    }
    postMessageToFlutter(
      `openPdfViewer:${JSON.stringify({ url, title: title ?? '' })}`,
    );
  },

  /**
   * アプリ内 WebView で HTML ページを開く（WebView 環境のみ）
   *
   * Flutter 側で `HkWebViewScreen` をプッシュ表示する。
   * 外部ブラウザを起動せず、アプリ内ヘッダー付きの WebView 画面として開く。
   * NP後払い 確認画面の利用規約リンク 2 本・案内バナーが主用途。
   *
   * メッセージ形式: `openInAppWebView:{"url":"...","title":"..."}`
   * 区切り文字衝突を避けるため JSON 文字列化する。
   *
   * セキュリティ二重防壁: Flutter 側 `parseOpenInAppWebViewMessage` と同じく
   * http/https 以外を拒否する。不正スキーム（javascript:/file:/data: 等）は
   * postMessage を送信せず warn のみ。
   *
   * @param url HTML ページの URL（http/https 限定）
   * @param title アプリ内 WebView のタイトル（省略時は空文字）
   */
  openInAppWebView: (url: string, title?: string): void => {
    // 制御文字（NULL/改行/タブ等）を含む URL を拒否（URL 偽装対策）
    // WHATWG URL パーサーは一部の制御文字を許容してしまうため明示的に弾く
    // eslint-disable-next-line no-control-regex
    if (/[\x00-\x1f\x7f]/.test(url)) {
      // eslint-disable-next-line no-console
      console.warn(
        '[appBridge.openInAppWebView] Rejected URL with control chars',
      );
      return;
    }
    // URL を WHATWG URL パーサーで検証（不正形式を弾く）
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      // eslint-disable-next-line no-console
      console.warn('[appBridge.openInAppWebView] Invalid URL');
      return;
    }
    // プロトコルホワイトリスト: Flutter 側と同じく http/https のみ
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      // eslint-disable-next-line no-console
      console.warn(
        '[appBridge.openInAppWebView] Rejected non-http(s) URL',
      );
      return;
    }
    postMessageToFlutter(
      `openInAppWebView:${JSON.stringify({ url, title: title ?? '' })}`,
    );
  },
};
